import { Component, inject, OnInit, HostListener } from '@angular/core';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { CommonModule } from '@angular/common';
import {
  CdfiProfile,
  CdfiProfileSummary,
} from '../../../my-profile/models/profile.model';
import { ProfileService } from '../../../my-profile/services/profile.service';
import { PaginationComponent } from '../../../../shared/components/pagination/pagination.component';
import { PageSizeComponent } from '../../../../shared/components/page-size/page-size.component';
import {
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  Subject,
  takeUntil,
  catchError,
  of,
} from 'rxjs';
import { ContainerSasUrl } from '../../../../shared/interfaces/company';
import { ImageService } from '../../../../shared/services/image-service/image.service';
import { SearchInputComponent } from '../../../../shared/components/search-input/search-input.component';
import { Router } from '@angular/router';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { PaginatedResponse } from '../../../../shared/interfaces/pagination';
import { SkeletonComponent } from '../../../../shared/components/skeleton/skeleton.component';
import { AuthService } from '../../../auth/services/auth.service';
import { ExportService } from '../../../../shared/services/export-service/export.service';
import { CommonService } from '../../../../shared/services/common/common.service';
import { ToastType } from '../../../../shared/interfaces/common';
import { PermissionCode } from '../../../../shared/enums/permission-code';

@Component({
  selector: 'app-cdfi-list',
  standalone: true,
  imports: [
    CommonModule,
    PaginationComponent,
    PageSizeComponent,
    SearchInputComponent,
    SvgIconComponent,
    SkeletonComponent,
  ],
  templateUrl: './cdfi-list.component.html',
  styleUrls: ['./cdfi-list.component.scss'],
})
export class CdfiListComponent extends MenuComponentBase implements OnInit {
  private profileService = inject(ProfileService);
  private imageService = inject(ImageService);
  private router = inject(Router);
  private authService = inject(AuthService);
  private exportService = inject(ExportService);
  private commonService = inject(CommonService);

  readonly isAnonymous = this.route.snapshot.data['anonymous'] === true;
  readonly isSmeUser = !this.isAnonymous && this.authService.isSMEUser();
  readonly isAdmin =
    !this.isAnonymous &&
    this.authService.hasPermission(PermissionCode.ADMIN_PRIVILEGES);
  searchQuery = '';
  loading = true;
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  cdfiProfiles: CdfiProfile[] = [];
  profileViewsCount = 0;
  assignedRecordsCount = 0;
  fundedAmount: string | number | null = null;
  fundedCdfiCount = 0;
  containerSasUrl!: ContainerSasUrl;
  isExportMenuOpen = false;
  isExporting = false;
  private searchSubject = new Subject<string>();

  override ngOnInit(): void {
    super.ngOnInit();
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((query) => {
        this.searchQuery = query;
        this.currentPage = 1;
        this.getCdfiProfileImages();
      });
    this.loadCdfiProfileImages();
  }

  loadCdfiProfileImages() {
    this.loading = true;

    const profilesRequest = this.isAnonymous
      ? this.profileService.getAnonymousCdfiProfiles(
          this.currentPage,
          this.pageSize,
        )
      : this.profileService.getCdfiProfiles(this.currentPage, this.pageSize);

    forkJoin({
      sasUrl: this.imageService.getContainerSasUrl(),
      profiles: profilesRequest,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ sasUrl, profiles }) => {
          this.containerSasUrl = sasUrl;
          this.applyProfilesResponse(profiles);
          this.loading = false;
        },
        error: (err) => {
          console.error('Error loading data:', err);
          this.loading = false;
        },
      });

    if (!this.isAnonymous && this.isSmeUser) {
      this.profileService
        .getCdfiProfileSummary()
        .pipe(
          takeUntil(this.destroy$),
          catchError((err) => {
            console.error('Error loading CDFI profile summary:', err);
            return of(null);
          }),
        )
        .subscribe((summary) => {
          this.applySummaryResponse(summary);
        });
    }
  }

  getImageUrl(path: string): string {
    if (!this.containerSasUrl || !path) return '';
    return `${path}?${this.containerSasUrl.sas_token}`;
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.getCdfiProfileImages();
  }

  onPageSizeChange(newPageSize: number): void {
    if (newPageSize !== this.pageSize) {
      this.pageSize = newPageSize;
      this.currentPage = 1;
      this.getCdfiProfileImages();
    }
  }

  onCdfiProfile(cdfiProfile: CdfiProfile) {
    if (cdfiProfile.connection_name) {
      this.router.navigate([
        this.isAnonymous ? '/cdfi/public/profile' : '/cdfi/profile',
        cdfiProfile.connection_name,
      ]);
    }
  }

  getCdfiProfileImages() {
    this.loading = true;
    const profilesRequest = this.isAnonymous
      ? this.profileService.getAnonymousCdfiProfiles(
          this.currentPage,
          this.pageSize,
          this.searchQuery,
        )
      : this.profileService.getCdfiProfiles(
          this.currentPage,
          this.pageSize,
          this.searchQuery,
        );

    profilesRequest.pipe(takeUntil(this.destroy$)).subscribe({
      next: (profiles) => {
        this.applyProfilesResponse(profiles);
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading CDFI profiles:', err);
        this.loading = false;
      },
    });
  }

  onSearchQueryChange(query: string) {
    this.searchSubject.next(query);
  }

  navigateToBusinessRecords(): void {
    this.router.navigate(['/business']);
  }

  getDescriptionPreview(description: string | null): string {
    if (!description) {
      return 'Contact this Organization to learn more about their services and offerings';
    }

    const textOnly = description.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ');
    return textOnly.length > 190
      ? `${textOnly.slice(0, 190).trim()}...`
      : textOnly;
  }

  getWebsiteHost(website: string | null): string {
    if (!website) {
      return '';
    }

    try {
      return new URL(
        website.startsWith('http') ? website : `https://${website}`,
      ).hostname.replace(/^www\./, '');
    } catch {
      return website;
    }
  }

  getMarketplaceTags(profile: CdfiProfile): {
    label: string;
    tooltip: string;
    icon: string;
    type: string;
    isEmpty: boolean;
  }[] {
    const tags: {
      label: string;
      tooltip: string;
      icon: string;
      type: string;
      isEmpty: boolean;
    }[] = [];

    // Email
    tags.push({
      label: profile.email || 'Email not provided',
      tooltip: profile.email
        ? `Email: ${profile.email}`
        : 'Email information not available',
      icon: 'email',
      type: 'email',
      isEmpty: !profile.email,
    });

    // Phone
    tags.push({
      label: profile.phone || 'Phone not provided',
      tooltip: profile.phone
        ? `Phone: ${profile.phone}`
        : 'Phone information not available',
      icon: 'phone',
      type: 'phone',
      isEmpty: !profile.phone,
    });

    // Website
    const websiteHost = profile.website
      ? this.getWebsiteHost(profile.website)
      : null;
    tags.push({
      label: websiteHost || profile.website || 'Website not provided',
      tooltip: profile.website
        ? `Website: ${profile.website}`
        : 'Website information not available',
      icon: 'website',
      type: 'website',
      isEmpty: !profile.website,
    });

    return tags;
  }

  formatCount(value?: number | null): string {
    return new Intl.NumberFormat('en-US').format(value ?? 0);
  }

  formatCurrency(value?: string | number | null): string {
    const amount = Number(value ?? 0);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(Number.isFinite(amount) ? amount : 0);
  }

  private applyProfilesResponse(
    response: PaginatedResponse<CdfiProfile>,
  ): void {
    this.totalItems = response.count;
    this.cdfiProfiles = response.results ?? [];
  }

  private applySummaryResponse(summary: CdfiProfileSummary | null): void {
    if (!summary) {
      this.profileViewsCount = 0;
      this.assignedRecordsCount = 0;
      this.fundedAmount = null;
      this.fundedCdfiCount = 0;
      return;
    }
    this.profileViewsCount = summary.profile_views?.cdfi_view_count ?? 0;
    this.assignedRecordsCount = summary.assigned_cdfi_count ?? 0;
    this.fundedAmount = summary.funded_status?.funded_amount ?? null;
    this.fundedCdfiCount = summary.funded_status?.funded_cdfi_count ?? 0;
  }

  activeTooltip: string | null = null;

  toggleTooltip(id: string, event: Event): void {
    event.stopPropagation();
    this.activeTooltip = this.activeTooltip === id ? null : id;
  }

  toggleExportMenu(event: Event): void {
    event.stopPropagation();
    this.isExportMenuOpen = !this.isExportMenuOpen;
  }

  exportData(type: 'csv' | 'json'): void {
    this.isExportMenuOpen = false;
    this.isExporting = true;

    this.exportService
      .exportCdfiProfiles(type)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const dateStr = new Date().toISOString().slice(0, 10);
          a.download = `cdfi-profiles-${dateStr}.${type}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);

          this.isExporting = false;
        },
        error: (err) => {
          console.error('Failed to export CDFI profiles:', err);
          this.isExporting = false;
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: 'Failed to export CDFI profiles. Please try again.',
          });
        },
      });
  }

  closeAllTooltips(): void {
    this.activeTooltip = null;
    this.isExportMenuOpen = false;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeAllTooltips();
  }
}
